'use client';

import type { CmsShellViewerMode } from '@/cms/chrome/shell-viewer-mode';
import {
  type CmsWorkspaceSidebarActionAppearance,
  resolveCmsWorkspaceSidebarHrefAppearance,
} from '@/cms/chrome/workspace-sidebar-action-appearance';
import { ClientLoadedWorkspaceHost } from '@/cms/components/blocks/workspace/client-loaded-workspace-host';
import type { WorkspaceStudioRenderModel } from '@/cms/components/blocks/workspace/workspace-1/contracts/server-model';
import type { WorkspaceLoadErrorCopy } from '@/cms/components/blocks/workspace/workspace-load-error';
import type { CmsWorkspaceLaunchDetail } from '@/cms/components/blocks/workspace/workspace-session-controller';
import { CmsPublicPageModeShell } from '@/cms/components/chrome/cms-public-page-mode-shell';
import type {
  CmsWorkspaceSidebarChildItem,
  CmsWorkspaceSidebarDestination,
  CmsWorkspaceSidebarItem,
  CmsWorkspaceSidebarModel,
} from '@/cms/components/chrome/cms-workspace-sidebar';
import type { CmsWorkspaceLaunchContext } from '@/cms/workspace/launch-context';
import { useWorkspaceTaskPaymentResume } from '@/site-runtime/checkout/use-workspace-task-payment-resume';
import {
  getSiteRuntimeNavigationBadge,
  getSiteRuntimeNavigationIcon,
  getSiteRuntimeNavigationTarget,
} from '@/site-runtime/navigation';
import type { SiteRuntimeTarget } from '@/site-runtime/runtime-config-contract';
import type { ReactNode } from 'react';
import { Suspense, useMemo } from 'react';
import { hasSiteRuntimeWorkspaceAuthIntentDocumentHint } from '../auth/site-runtime-workspace-auth-intent';
import type {
  SiteRuntimeGlobalSnapshot,
  SiteRuntimeNavigationItem,
  SiteRuntimeNavigationLeaf,
} from '../types';
import {
  useSiteRuntimeActionContext,
  useSiteRuntimeWorkspaceActionCallbacks,
  useSiteRuntimeWorkspaceDialogCallbacks,
} from './site-runtime-entry-action-controller';
import { SiteRuntimeWorkspacePreloader } from './site-runtime-workspace-preloader';

type WorkspaceSidebarAction =
  | {
      target: SiteRuntimeTarget;
      type: 'runtime_target';
    }
  | { type: 'workspace_login' };

type WorkspaceSidebarActionTargets = Map<string, WorkspaceSidebarAction>;

function resolveRuntimeSidebarActionAppearance(
  target: SiteRuntimeTarget
): CmsWorkspaceSidebarActionAppearance {
  if (target.type === 'open_url') {
    return resolveCmsWorkspaceSidebarHrefAppearance(target.href);
  }

  if (
    target.type === 'custom_entry_action' &&
    target.action.type === 'open_popup' &&
    target.action.popupId === 'paywall' &&
    target.action.branchSlug === 'upgradeRequired'
  ) {
    return 'pricing-action';
  }

  return 'default';
}

function toSidebarDestination(params: {
  actionKey: string;
  actionTargets: WorkspaceSidebarActionTargets;
  target: SiteRuntimeTarget;
}): CmsWorkspaceSidebarDestination {
  if (params.target.type === 'open_url') {
    return { type: 'href', href: params.target.href };
  }

  params.actionTargets.set(params.actionKey, {
    target: params.target,
    type: 'runtime_target',
  });
  return { type: 'action', actionKey: params.actionKey };
}

function toSidebarChildItem(params: {
  actionTargets: WorkspaceSidebarActionTargets;
  link: SiteRuntimeNavigationLeaf;
}): CmsWorkspaceSidebarChildItem {
  const target = getSiteRuntimeNavigationTarget(params.link, 'sidebar');
  const icon = getSiteRuntimeNavigationIcon(params.link, 'sidebar');
  return {
    label: params.link.label,
    destination: toSidebarDestination({
      actionKey: `sidebar:${params.link.id}`,
      actionTargets: params.actionTargets,
      target,
    }),
    icon: icon?.iconKey,
    badgeLabel: getSiteRuntimeNavigationBadge(params.link, 'sidebar'),
  };
}

function toSidebarItem(params: {
  actionTargets: WorkspaceSidebarActionTargets;
  link: SiteRuntimeNavigationItem;
}): CmsWorkspaceSidebarItem {
  const childGroups = params.link.childGroups.map((group) => ({
    title: group.title,
    links: group.links.map((link) =>
      toSidebarChildItem({
        actionTargets: params.actionTargets,
        link,
      })
    ),
  }));
  const target = getSiteRuntimeNavigationTarget(params.link, 'sidebar');
  const icon = getSiteRuntimeNavigationIcon(params.link, 'sidebar');

  return {
    id: params.link.id,
    label: params.link.label,
    appearance: resolveRuntimeSidebarActionAppearance(target),
    destination: toSidebarDestination({
      actionKey: `sidebar:${params.link.id}`,
      actionTargets: params.actionTargets,
      target,
    }),
    icon: icon?.iconKey,
    badgeLabel: getSiteRuntimeNavigationBadge(params.link, 'sidebar'),
    children: childGroups.flatMap((group) => group.links),
    childGroups,
  };
}

export function buildSiteRuntimeWorkspaceSidebarModel(
  global: SiteRuntimeGlobalSnapshot
): {
  actionTargets: WorkspaceSidebarActionTargets;
  sidebar: CmsWorkspaceSidebarModel;
} {
  const actionTargets: WorkspaceSidebarActionTargets = new Map();
  const brandLabel = global.shell.header.brand.name?.trim();
  if (!brandLabel) {
    throw new Error('Workspace sidebar brand label is not configured.');
  }

  const loginActionKey = 'sidebar:account:login';
  if (global.shell.header.account.loginLabel) {
    actionTargets.set(loginActionKey, { type: 'workspace_login' });
  }
  const sidebarItems = global.shell.navigation.items.filter(
    (item) => item.surfaces.sidebar
  );

  return {
    actionTargets,
    sidebar: {
      brandLabel,
      brandDestination: toSidebarDestination({
        actionKey: 'sidebar:brand',
        actionTargets,
        target: global.shell.header.brand.homeTarget,
      }),
      utilityCopy: global.shell.sidebar.utilityCopy,
      navItems: sidebarItems
        .filter((item) => item.surfaces.sidebar?.placement === 'top')
        .map((link) => toSidebarItem({ actionTargets, link })),
      bottomNavItems: sidebarItems
        .filter((item) => item.surfaces.sidebar?.placement === 'bottom')
        .map((link) => toSidebarItem({ actionTargets, link })),
      utilityItems: global.shell.header.account.loginLabel
        ? [
            {
              id: 'login',
              kind: 'login',
              label: global.shell.header.account.loginLabel,
              activation: {
                type: 'navigation_action',
                actionKey: loginActionKey,
              },
            },
          ]
        : [],
    },
  };
}

export function SiteRuntimeWorkspaceHost({
  errorCopy,
  initialLaunchDetail,
  locale,
  shellHero,
}: {
  errorCopy: WorkspaceLoadErrorCopy;
  initialLaunchDetail: CmsWorkspaceLaunchDetail;
  locale: string;
  shellHero: WorkspaceStudioRenderModel['shell']['hero'];
}) {
  const runtimeDialogCallbacks = useSiteRuntimeWorkspaceDialogCallbacks();
  const runtimeActionCallbacks = useSiteRuntimeWorkspaceActionCallbacks();

  return (
    <ClientLoadedWorkspaceHost
      errorCopy={errorCopy}
      initialLaunchDetail={initialLaunchDetail}
      locale={locale}
      runtimeDialogCallbacks={runtimeDialogCallbacks}
      runtimeActionCallbacks={runtimeActionCallbacks}
      shellHero={shellHero}
      surface="workspace-page"
    />
  );
}

function WorkspaceTaskPaymentResumeEffect({ locale }: { locale: string }) {
  useWorkspaceTaskPaymentResume(locale);
  return null;
}

export function SiteRuntimeWorkspaceShell({
  announcement,
  canEnterWorkspaceMode,
  children,
  currentPath,
  footer,
  initialWorkspaceMode,
  standardHeader,
  storageUploadOrigin,
  viewerMode = 'live',
  workspaceContent,
  workspaceLaunchContext,
}: {
  announcement?: ReactNode;
  canEnterWorkspaceMode: boolean;
  children: ReactNode;
  currentPath: string;
  footer: ReactNode;
  initialWorkspaceMode: boolean;
  standardHeader: ReactNode;
  storageUploadOrigin?: string;
  viewerMode?: CmsShellViewerMode;
  workspaceContent?: ReactNode;
  workspaceLaunchContext?: CmsWorkspaceLaunchContext;
}) {
  const {
    executeTarget,
    global,
    locale,
    openLogin,
    page,
    suppressWorkspaceAutoLaunch,
  } = useSiteRuntimeActionContext();
  const sidebarModel = useMemo(
    () => buildSiteRuntimeWorkspaceSidebarModel(global),
    [global]
  );

  return (
    <>
      <Suspense fallback={null}>
        <WorkspaceTaskPaymentResumeEffect locale={locale} />
      </Suspense>
      <SiteRuntimeWorkspacePreloader
        enabled={canEnterWorkspaceMode && !initialWorkspaceMode}
        locale={locale}
        toolId={page?.boundToolId ?? null}
        storageUploadOrigin={storageUploadOrigin}
      />
      <CmsPublicPageModeShell
        announcement={announcement}
        canEnterWorkspaceMode={canEnterWorkspaceMode}
        currentPath={currentPath}
        footer={footer}
        initialWorkspaceMode={initialWorkspaceMode}
        locale={locale}
        onSidebarNavigationAction={(actionKey) => {
          const action = sidebarModel.actionTargets.get(actionKey);
          if (!action) {
            throw new Error(
              `Workspace sidebar action ${actionKey} is missing.`
            );
          }
          if (action.type === 'workspace_login') {
            openLogin({ mode: 'workspace', scene: 'workspaceAccess' });
            return;
          }
          executeTarget(action.target);
        }}
        sidebar={sidebarModel.sidebar}
        standardHeader={standardHeader}
        suppressAuthenticatedAutoLaunch={
          suppressWorkspaceAutoLaunch ||
          hasSiteRuntimeWorkspaceAuthIntentDocumentHint()
        }
        viewerMode={viewerMode}
        workspaceContent={workspaceContent}
        workspaceLaunchContext={workspaceLaunchContext}
      >
        {children}
      </CmsPublicPageModeShell>
    </>
  );
}
